// components/widgets/Identity/FutureIdentity.jsx
import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import stocai from "../../../../../assets/PandaProfile.png";
import radial from "../../../../../assets/Group2.png";
import FutureYou from "./FutureYou/FutureYou";
import FutureCompletion from "./FutureYou/CompletionPages/FutureCompletion";
import FutureFinalResult from "./FutureYou/CompletionPages/FutureFinalResult";
import FutureIdentityCard from "./FutureYou/FutureIdentityCard";
import { progressService } from "../../../../../services/progressService";
import { UserContext } from "../../../../../UserContext";
import { FaChevronUp, FaChevronDown } from "react-icons/fa";


const CURRENT_IDENTITY_SUBSECTION_ID =
  "f90552a1-12fd-4f0b-b4f5-17e258b84b97";

const apiBaseURL =
  process.env.REACT_APP_BACKEND_API_URL || "http://localhost:8000";

const FutureIdentity = ({
  userId,
  subsectionId,
  currentIdentitySubsectionId = CURRENT_IDENTITY_SUBSECTION_ID,
  initialData,
  onComplete,
  onToggleImmersive,
  actions,
  onPhaseChange,
  onSubsectionChange,
  course,
}) => {
  const [step, setStep] = useState(1);
  const [subStep, setSubStep] = useState(1);
  const [essentialsField, setEssentialsField] = useState(0);
  const [completionStep, setCompletionStep] = useState(0);
  const [futureYouReady, setFutureYouReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [isRecording, setIsRecording] = useState(false);


  const { user } = useContext(UserContext);

  const [userFutureData, setUserFutureData] = useState({
    designation: "",
    location: "",
    salary: "",
    timeline: "",
    knownFor: [],
    profileImage: null,
    futureVoiceNote: null,
  });

  const [currentIdentityProfileImage, setCurrentIdentityProfileImage] =
    useState(null);
  const [currentIdentityVoiceNote, setCurrentIdentityVoiceNote] =
    useState(null);

  // full current identity payload, needed to overwrite futureVoiceNote
  const [currentIdentityPayload, setCurrentIdentityPayload] = useState(null);

  const [currentKnownForInput, setCurrentKnownForInput] = useState("");

  const userName =
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : user?.firstName || user?.lastName || "";

  const currentDate = new Date();
  const currentMonthYear = currentDate
    .toLocaleDateString("en-US", { month: "short", year: "numeric" })
    .toUpperCase();

  const nextSubsectionId = useMemo(() => {
    if (!course?.modules?.length || !subsectionId) return null;

    const orderedSubsections = course.modules
      .slice()
      .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
      .flatMap((module) =>
        (module?.subsections || [])
          .slice()
          .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
      );

    const currentIndex = orderedSubsections.findIndex(
      (sub) => sub?.id === subsectionId
    );
    if (currentIndex === -1) return null;

    return orderedSubsections[currentIndex + 1]?.id || null;
  }, [course, subsectionId]);

  // ===== Load FUTURE identity widget data =====
  useEffect(() => {
    async function loadFutureWidget() {
      try {
        if (!userId || !subsectionId) return;
        const futureProgress = await progressService.getUserProgress(
          userId,
          subsectionId
        );
        if (futureProgress?.data) {
          const payload = futureProgress.data.payload || futureProgress.data;
          setUserFutureData((prev) => ({
            ...prev,
            designation: payload.designation || "",
            location: payload.location || "",
            salary: payload.salary || "",
            timeline: payload.timeline || "",
            knownFor: Array.isArray(payload.knownFor) ? payload.knownFor : [],
            profileImage: payload.profileImage || prev.profileImage,
            futureVoiceNote: payload.futureVoiceNote || prev.futureVoiceNote,
          }));
        }
      } catch (err) {
        console.error("Error loading future identity widget data:", err);
      }
    }

    loadFutureWidget();
  }, [userId, subsectionId]);

  // ===== Load CURRENT identity data =====
  useEffect(() => {
    async function loadCurrentIdentity() {
      try {
        if (!userId || !currentIdentitySubsectionId) return;
        const currentRec = await progressService.getUserProgress(
          userId,
          currentIdentitySubsectionId
        );
        const currentPayload =
          currentRec?.data?.payload || currentRec?.data || {};

        setCurrentIdentityPayload(currentPayload);

        if (currentPayload.profileImage) {
          setCurrentIdentityProfileImage(currentPayload.profileImage);
        }
        if (currentPayload.futureVoiceNote) {
          setCurrentIdentityVoiceNote(currentPayload.futureVoiceNote);
        }
      } catch (err) {
        console.error(
          "Error loading current identity for FutureIdentity intro:",
          err
        );
      }
    }

    loadCurrentIdentity();
  }, [userId, currentIdentitySubsectionId]);

  const resolvedProfileImage =
    userFutureData.profileImage ||
    currentIdentityProfileImage ||
    user?.profilePicture ||
    null;

  const resolvedFutureVoiceNote =
    userFutureData.futureVoiceNote || currentIdentityVoiceNote || null;

  // ===== Immersive mode =====
  useEffect(() => {
    if (typeof onToggleImmersive !== "function") return;
    if (step === 1) onToggleImmersive(false);
    else onToggleImmersive(true);
    return () => {
      onToggleImmersive(false);
    };
  }, [step, onToggleImmersive]);

  // completion fullscreen
  useEffect(() => {
    if (typeof onToggleImmersive !== "function") return;
    if (completionStep === 0) return;
    onToggleImmersive(true);
    return () => {
      onToggleImmersive(false);
    };
  }, [completionStep, onToggleImmersive]);

  // ===== Phase reporting =====
  useEffect(() => {
    if (!onPhaseChange) return;
    if (step === 1) onPhaseChange("intro");
    else onPhaseChange("immersive");
  }, [step, onPhaseChange]);

  // ===== Save progress (Future Identity widget) =====
  const saveProgress = useCallback(
    async (status = "in_progress") => {
      if (!userId || !subsectionId) return;
      setIsLoading(true);
      setError(null);
      try {
        const dataToSave = {
          type: "widget",
          payload: {
            designation: userFutureData.designation,
            location: userFutureData.location,
            salary: userFutureData.salary,
            timeline: userFutureData.timeline,
            knownFor: userFutureData.knownFor,
            futureVoiceNote: userFutureData.futureVoiceNote,
          },
        };
        await progressService.saveProgress(
          userId,
          subsectionId,
          dataToSave,
          status
        );
      } catch (err) {
        setError("Failed to save progress. Please try again.");
        console.error("Save progress error:", err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [userId, subsectionId, userFutureData]
  );

  // ===== Upload future voice note (Azure) and sync to CURRENT identity =====
  const handleFutureVoiceUpload = useCallback(
    async (audioBlobOrNull) => {
      // Clear case: remove voice note from both Future and Current Identity
      if (!audioBlobOrNull) {
        setUserFutureData((prev) => ({ ...prev, futureVoiceNote: null }));
        setCurrentIdentityVoiceNote(null);

        if (
          userId &&
          currentIdentitySubsectionId &&
          currentIdentityPayload
        ) {
          const updatedCurrentPayload = {
            ...currentIdentityPayload,
            futureVoiceNote: null,
          };
          try {
            await progressService.saveProgress(
              userId,
              currentIdentitySubsectionId,
              { type: "widget", payload: updatedCurrentPayload },
              "in_progress"
            );
            setCurrentIdentityPayload(updatedCurrentPayload);
          } catch (err) {
            console.error(
              "Failed to clear futureVoiceNote in current identity:",
              err
            );
          }
        }
        return;
      }

      if (!userId) {
        setError("User not authenticated");
        return;
      }

      setUploading(true);
      setError(null);

      try {
        const timestamp = Date.now();
        const filename = `future_voice_note_${timestamp}.webm`;

        const uploadUrlResponse = await fetch(`${apiBaseURL}/upload-url`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: userId,
            filename,
            minutes_valid: 15,
          }),
        });

        if (!uploadUrlResponse.ok) {
          const errorData = await uploadUrlResponse.json().catch(() => ({}));
          throw new Error(errorData.detail || "Failed to get upload URL");
        }

        const { uploadUrl, blobName } = await uploadUrlResponse.json();

        const azureUploadResponse = await fetch(uploadUrl, {
          method: "PUT",
          headers: {
            "x-ms-blob-type": "BlockBlob",
            "Content-Type": "audio/webm",
          },
          body: audioBlobOrNull,
        });

        if (!azureUploadResponse.ok) {
          throw new Error(`Azure upload failed: ${azureUploadResponse.status}`);
        }

        const readUrlResponse = await fetch(
          `${apiBaseURL}/read-url?blob_name=${encodeURIComponent(blobName)}`
        );

        if (!readUrlResponse.ok) {
          throw new Error("Failed to get read URL");
        }

        const { readUrl } = await readUrlResponse.json();

        // 1) Update Future Identity local state
        setUserFutureData((prev) => ({
          ...prev,
          futureVoiceNote: readUrl,
        }));

        // 2) Update CURRENT Identity record (the source you pasted)
        if (
          currentIdentityPayload &&
          currentIdentitySubsectionId &&
          userId
        ) {
          const updatedCurrentPayload = {
            ...currentIdentityPayload,
            futureVoiceNote: readUrl,
          };

          await progressService.saveProgress(
            userId,
            currentIdentitySubsectionId,
            { type: "widget", payload: updatedCurrentPayload },
            "in_progress"
          );

          setCurrentIdentityPayload(updatedCurrentPayload);
          setCurrentIdentityVoiceNote(readUrl);
        }
      } catch (err) {
        setError(err.message || "Failed to upload voice note. Please try again.");
        console.error("Future voice upload error:", err);
      } finally {
        setUploading(false);
      }
    },
    [userId, currentIdentitySubsectionId, currentIdentityPayload]
  );

  // ===== Validation =====
  const isStepComplete = useCallback(() => {
    if (subStep === 1) {
      if (essentialsField === 0 && !userFutureData.designation?.trim())
        return false;
      if (essentialsField === 1 && !userFutureData.salary) return false;
      if (essentialsField === 2 && !userFutureData.timeline) return false;
      if (essentialsField === 3 && !userFutureData.location?.trim())
        return false;
      return true;
    }

    if (subStep === 2) {
      if (!userFutureData.knownFor || userFutureData.knownFor.length < 4)
        return false;
      return true;
    }

    if (subStep === 3) {
      // Future note step: accept either saved future note or carried‑over current identity note
      return !!(userFutureData.futureVoiceNote || resolvedFutureVoiceNote);
    }
      
    return false;
  }, [subStep, essentialsField, userFutureData, resolvedFutureVoiceNote]);

  // ===== Navigation handlers =====
  const handleNext = useCallback(
    async () => {
      if (step === 1) {
        setStep(2);
        setFutureYouReady(true);
        return;
      }

      if (!isStepComplete()) {
        setError("Please complete all required fields");
        return;
      }

      try {
        setError(null);

        if (subStep === 1 && essentialsField < 3) {
          await saveProgress("in_progress");
          setEssentialsField(essentialsField + 1);
          return;
        }

        const status = subStep === 3 ? "completed" : "in_progress";
        await saveProgress(status);

        if (subStep < 3) {
          setSubStep(subStep + 1);
          setEssentialsField(0);
        } else {
          setCompletionStep(1);
        }
      } catch (err) {
        console.error("Error in handleNext:", err);
      }
    },
    [step, subStep, essentialsField, saveProgress, isStepComplete]
  );

  const handleBack = useCallback(
    async () => {
      if (step === 1) return;

      if (subStep === 1 && essentialsField > 0) {
        await saveProgress("in_progress");
        setEssentialsField(essentialsField - 1);
        return;
      }

      if (subStep > 1) {
        await saveProgress("in_progress");
        setSubStep(subStep - 1);
        setError(null);
      }
    },
    [subStep, essentialsField, saveProgress, step]
  );

  // ===== Global Enter -> Next =====
  useEffect(() => {
    const onKeyDown = (e) => {
      if (step !== 2) return;
      if (e.key === "Enter" && isStepComplete()) {
        e.preventDefault();
        handleNext();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [step, isStepComplete, handleNext]);

  // ===== Parent button configuration =====
  useEffect(() => {
    if (!actions) return;
    const visible = step !== 2 || futureYouReady;
    actions.setPrimary({
      label: "Next Step",
      onClick: handleNext,
      disabled: false,
      loading: false,
      visible,
    });
    return () => actions.clearPrimary?.();
  }, [step, actions, futureYouReady, handleNext]);

  const handleInputChange = (field, value) => {
    setUserFutureData((prev) => ({ ...prev, [field]: value }));
  };

  // ===== Completion screens =====
  if (completionStep === 1) {
    return (
      <FutureCompletion
        futureData={{
          ...userFutureData,
          futureVoiceNote: resolvedFutureVoiceNote,
        }}
        userName={userName}
        profileImage={resolvedProfileImage}
        onNext={() => setCompletionStep(2)}
      />
    );
  }

  if (completionStep === 2) {
    return (
      <FutureFinalResult
        futureData={{
          ...userFutureData,
          futureVoiceNote: resolvedFutureVoiceNote,
        }}
        userName={userName}
        profileImage={resolvedProfileImage}
        onComplete={() => {
          if (nextSubsectionId) {
            onSubsectionChange?.(nextSubsectionId);
            return;
          }
          onComplete?.();
        }}
      />
    );
  }

  // ===== INTRO SCREEN =====
if (step === 1) {
  const introProfileImage = resolvedProfileImage;

  return (
    <div className="w-full">
      <div className="rounded-2xl bg-white p-6 md:p-8 mb-6">
        <h2 className="text-xl md:text-2xl lg:text-[32px] border-b pb-3 font-medium font-gotham text-[#323232] mb-6 mr-[-2rem]">
          Build Your Future Identity
        </h2>

        <p className="text-[#323232] font-gotham text-base md:text-lg leading-relaxed mb-2">
          Envision your future identity as a next step in career
          progression. This stage builds you to articulate the skills,
          qualities, and professional attributes you aspire to embody in
          your chosen field.
        </p>

        <p className="text-[#323232] font-gotham text-base md:text-lg leading-relaxed mb-4">
          By clearly defining your desired future state, you create a
          powerful roadmap for growth and transformation, ensuring every
          decision and action supports your long-term vision and goals.
        </p>

        <div className="flex items-center justify-center gap-8 max-w-4xl mx-auto mt-12">
          {/* CURRENT STATE CARD */}
          <div className="relative w-[261px] h-[300px] rounded-[25px] border border-gray-300 opacity-30 overflow-hidden bg-white">
            <div className="absolute top-0 left-0 w-full h-[146px] bg-[#0D1111] rounded-t-[24px]">
              <h4 className="text-xs md:text-[14px] font-gotham font-semibold text-white mb-4 uppercase tracking-wider text-center pt-6">
                CURRENT STATE
              </h4>
            </div>
            <div className="relative z-10 flex flex-col items-center pt-16">
              <div className="w-[164px] h-[164px] bg-gray-200 rounded-full flex items-center justify-center mb-2 overflow-hidden border-8 border-white">
                {introProfileImage ? (
                  <img
                    src={introProfileImage}
                    alt="Current"
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <svg
                    className="w-12 h-12 text-gray-400"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                  </svg>
                )}
              </div>
              <h3 className="font-gotham text-lg md:text-[23px] font-semibold text-[#1C1C1C] mb-1">
                {userName}
              </h3>
              <p className="font-gotham text-xs md:text-[14px] font-normal text-[#1C1C1C]">
                {currentMonthYear}
              </p>
            </div>
          </div>

          {/* CONNECTOR */}
          <div className="flex flex-col items-center justify-center">
            <svg
              width="225"
              height="10"
              viewBox="0 0 225 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="4" cy="5" r="4" fill="#B2B2B2" />
              <line
                x1="4"
                y1="5"
                x2="221"
                y2="5"
                stroke="#B2B2B2"
                strokeWidth="2"
                strokeDasharray="13 13"
              />
              <circle cx="221" cy="5" r="4" fill="#B2B2B2" />
            </svg>
          </div>

          {/* FUTURE STATE CARD - Overlay */}
            <div className="relative w-[261px] h-[300px] rounded-[25px] border border-[#D9D9D9] overflow-hidden bg-white">
              <div className="absolute top-0 left-0 w-full h-[146px] bg-[#54B0AF] rounded-t-[24px]">
                <h4 className="text-xs font-gotham font-semibold text-white mb-4 uppercase tracking-wider text-center pt-6">
                  FUTURE STATE
                </h4>
              </div>
              <div className="relative z-10 flex flex-col items-center pt-16">
                <div className="relative w-[164px] h-[164px] mb-2 rounded-full overflow-hidden">
                  
                  {introProfileImage ? (
                    <img
                      src={introProfileImage}
                      alt="Future"
                      className="w-full h-full object-cover rounded-full"
                    />
                  ) : (
                    <div className="flex items-center justify-center w-full h-full bg-white">
                      <svg
                        className="w-10 h-10 text-gray-400"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                      </svg>
                    </div>
                  )}

                
                  <img
                    src={radial}
                    alt="Promoted"
                    className="absolute top-0 left-0 z-20 pointer-events-none"
                    style={{
                      width: '174px',
                      height: '174px',
                      objectFit: 'contain'
                    }}
                  />
                </div>

                <h3 className="font-gotham text-lg md:text-[23px] font-semibold text-[#1C1C1C] mb-1">
                  {userName}
                </h3>
                <p className="font-gotham text-xs md:text-[14px] font-normal text-[#1C1C1C]">
                  {new Date().getFullYear()} & BEYOND
                </p>
              </div>
            </div>

           </div>
      </div>
    </div>
  );
}


  // ===== MAIN FORM =====
  return (
    <div className="w-full h-full flex flex-col">
      <div className="w-full h-full flex flex-col">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:items-stretch">
          <div className="lg:col-span-8 flex flex-col">
            <div className="bg-white rounded-2xl shadow-md flex-1 flex flex-col">
              <div className="p-6 md:p-8">
                <h1 className="text-2xl font-medium font-gotham text-gray-900">
                  Map Your Vision – Future You
                </h1>
              </div>

              <div className="p-6 md:p-8">
                <div className="flex items-start gap-4 flex-1">
                  <img
                    src={stocai}
                    alt="Coach"
                    className="w-12 h-12 md:w-16 md:h-16 rounded-full flex-shrink-0"
                  />
                  <div className="flex-1">
                  <FutureYou
                    userFutureData={userFutureData}
                    onInputChange={handleInputChange}
                    subStep={subStep}
                    essentialsField={essentialsField}
                    futureVoiceNote={resolvedFutureVoiceNote}
                    currentKnownForInput={currentKnownForInput}
                    onCurrentKnownForInputChange={setCurrentKnownForInput}
                    onEnterNext={handleNext}
                    onFutureVoiceUpload={handleFutureVoiceUpload}
                    uploading={uploading}
                    onRecordingChange={setIsRecording}   // NEW
                  />
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 md:px-8 py-6 border-t border-gray-100 flex justify-between items-center">
              <div />
              <div className="flex-1 text-center">
                {error && (
                  <p className="text-sm text-red-600 font-gotham">{error}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleBack}
                  disabled={
                    step === 1 || (subStep === 1 && essentialsField === 0)
                  }
                  className="inline-flex items-center gap-2 px-2 py-2 font-gotham bg-[#B6B6B6] rounded-full hover:bg-[#54B0AF] font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <FaChevronUp className="w-4 h-4 font-gotham -rotate-90 text-white" />
                </button>

                <button
                  onClick={handleNext}
                  disabled={isLoading || uploading || isRecording || !isStepComplete()}
                  className="inline-flex items-center gap-2 px-2 py-2 font-gotham bg-[#B6B6B6] rounded-full hover:bg-[#54B0AF] font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <FaChevronDown className="w-4 h-4 font-gotham -rotate-90 text-white" />
                </button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-4 flex flex-col max-h-none">
            <FutureIdentityCard
              futureData={{
                ...userFutureData,
                futureVoiceNote: resolvedFutureVoiceNote,
              }}
              userName={userName}
              profileImage={resolvedProfileImage}
              currentInput={currentKnownForInput}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FutureIdentity;
